import React from 'react';
import { Button, Flex, InlineLink, PageSpinner, Text } from '@fullstory/components';
import { useLibraryObjectSearch } from '../../hooks';
import { intl } from '@fullstory/intl';
import { Link, useHistory } from 'react-router-dom';
import { EmptyState } from '../EmptyState';
import { FGACButton, getFGACButtonExplorerRoleProps } from '../FGACButton';
import { AccessDeniedMessage } from '../AccessDeniedMessage';
import { useFlags } from '@fullstory/react-hooks';

interface LibraryTableNoResultsProps
  extends Pick<
    ReturnType<typeof useLibraryObjectSearch>,
    'error' | 'loading' | 'objects' | 'totalRecords'
  > {
  /**
   * Needs to be provided when the context of the search is within a space.
   */
  space?: {
    /**
     * When a space does not have any objects in it.
     */
    zeroStateDescription: string;
    /**
     * When a space has objects in it, but the search yielded no results.
     */
    noResultsDescription: string;
    /**
     * If a callback is provided, renders a primary Add from Library action button.
     */
    onZeroStateAddFromLibrary?: () => void;
  };
  /**
   * If a callback is provided, the button is shown on a search result that came back with zero results.
   */
  onClearFiltersClick?: () => void;
  /**
   * If a callback is provided, the button is shown on a search result that came back with zero results.
   */
  onAddFromLibraryClick?: () => void;
  /**
   * If provided, the link is shown on a search result that came back with zero results.
   */
  expandToGlobalSearchLink?: string;
  /**
   * If provided, the button is shown on a search result that came back with zero results.
   */
  returnToSpaceLink?: string;
  /**
   * Retry button in the error state.
   */
  onRetryClick: () => void;
  /**
   * The link or action to "start over" (clear) the search.
   *
   * The InlineLink component is used for this, either a link (to) or onClick action
   * must be supplied to handle it.
   */
  startOver: React.ComponentProps<typeof InlineLink>;

  /**
   * Whether the user has the permission to modify space objects
   *
   * If false, the "Add from library" button is disabled with a tooltip
   */
  canModifySpacesObjects: boolean;
}

/**
 * Handles loading states for useLibraryObjectSearch.
 *
 * Returns null when a successful search with results has been returned.
 */
export const LibraryTableNoResults: React.FC<LibraryTableNoResultsProps> = ({
  error,
  loading,
  objects,
  totalRecords,
  space,
  onClearFiltersClick,
  onAddFromLibraryClick,
  expandToGlobalSearchLink,
  returnToSpaceLink,
  onRetryClick,
  startOver,
  canModifySpacesObjects,
}) => {
  const history = useHistory();
  const flags = useFlags();
  const newFSTAColors = flags.featureFlags().AppNewFstaColors;

  // check for not loading so retries of errors transitions to the page spinner during retry/refretch
  if (error && !loading) {
    return (
      <EmptyState
        icon="no-data"
        heading={intl.formatMessage({ defaultMessage: 'An error occurred', id: 'ox304v' })}
        description={intl.formatMessage({
          defaultMessage:
            'We encountered an error while performing your search. You can either retry your search or start over.',
          id: 'ESkXQF',
        })}
      >
        <Flex column gap={16}>
          <Button variant="fill-secondary" onClick={onRetryClick} iconLeft="refresh">
            {intl.formatMessage({ defaultMessage: 'Retry', id: '62nsdy' })}
          </Button>
          <InlineLink {...startOver}>
            {intl.formatMessage({
              defaultMessage: 'Start your search over',
              id: '2HmPX4',
            })}
          </InlineLink>
        </Flex>
      </EmptyState>
    );
  }

  // prior to the first fetch returning, don't render anything yet to avoid any flashes
  if (!objects || loading) {
    return <PageSpinner />;
  }

  // objects have been fetched and there are zero results
  // if there are total records (but no matching records) show a zero results view,
  // else zero state
  if (!objects.length) {
    if (totalRecords) {
      // objects exist, not the zero state
      const description = space
        ? space.noResultsDescription
        : intl.formatMessage({
            defaultMessage: 'Nothing in your organization matches this search.',
            id: 'WIN6c+',
          });

      return (
        <EmptyState
          icon={space ? 'empty-space' : 'no-data'}
          heading={intl.formatMessage({
            defaultMessage: 'No match found',
            id: '3279hS',
          })}
          description={description}
        >
          <Flex column gap={16}>
            {onClearFiltersClick && (
              <Button variant="fill-secondary" onClick={onClearFiltersClick}>
                {intl.formatMessage({ defaultMessage: 'Clear Filters', id: 'MsGXc3' })}
              </Button>
            )}
            {onAddFromLibraryClick && (
              <FGACButton
                disabled={!canModifySpacesObjects}
                variant={newFSTAColors ? 'fill-primary' : 'fill-secondary'}
                onClick={onAddFromLibraryClick}
                iconLeft="plus"
                tooltip={
                  <AccessDeniedMessage suggestion="none" viewOnly={!canModifySpacesObjects} />
                }
                {...getFGACButtonExplorerRoleProps(canModifySpacesObjects)}
              >
                {intl.formatMessage({ defaultMessage: 'Add from Library', id: 'uM67F1' })}
              </FGACButton>
            )}
            {!!expandToGlobalSearchLink && (
              <Text appearance="bodySmallBold">
                <Link to={expandToGlobalSearchLink}>
                  {intl.formatMessage({
                    defaultMessage: 'Try this search globally',
                    id: '9y7jnw',
                  })}
                </Link>
              </Text>
            )}
            {returnToSpaceLink && (
              <Button
                variant="outline"
                iconLeft="arrow-left"
                onClick={() => history.push(returnToSpaceLink)}
              >
                {intl.formatMessage({ defaultMessage: 'Return to space', id: 'bAhaIo' })}
              </Button>
            )}
          </Flex>
        </EmptyState>
      );
    } else if (space) {
      // shouldn't have a case of no objects on the org level (eg canned)
      return (
        <EmptyState
          icon="empty-space"
          heading={intl.formatMessage({
            defaultMessage: 'This space is empty',
            id: '93Bbjj',
          })}
          description={space.zeroStateDescription}
        >
          {space.onZeroStateAddFromLibrary && (
            <FGACButton
              disabled={!canModifySpacesObjects}
              variant="fill-primary"
              onClick={space.onZeroStateAddFromLibrary}
              tooltip={<AccessDeniedMessage suggestion="none" viewOnly={!canModifySpacesObjects} />}
              {...getFGACButtonExplorerRoleProps(canModifySpacesObjects)}
            >
              {intl.formatMessage({ defaultMessage: 'Add from Library', id: 'uM67F1' })}
            </FGACButton>
          )}
        </EmptyState>
      );
    }
  }

  return null;
};
